import React from "react";
import "./index.css";

import Icon from "../Icon";

/* Helpers */
import { handleTimePeriod, latestFirst } from "../../helpers/date";

export default ({ propName, items, expanded, onExpand, edit, remove, archive }) => {

    return renderList(propName, items, expanded, onExpand, edit, remove, archive);

}

const renderList = (propName, items, expanded, onExpand, edit, remove, archive) => {

    let sorted = items.sort(latestFirst);

    return sorted.map((item, index) => {

        if (item.archived) return <div key={index}></div>;

        const canEdit = !item.completed;
        const onCardClick = () => canEdit ? edit(item, index, propName) : null;

        return(
            <div
                className={`accordion-wrapper${canEdit ? " accordion-wrapper--clickable" : ""}`}
                key={index}
                style={{ marginBottom: index+1 < sorted.length ? 20 : 0, borderColor: expanded.includes(index) ? "var(--jc-coral-500)" : "var(--jc-ink-100)" }}
                onClick={onCardClick}
                role={canEdit ? "button" : undefined}
                tabIndex={canEdit ? 0 : undefined}
                onKeyDown={canEdit ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCardClick(); } } : undefined}
            >
                <div className="accordion-actions-corner" onClick={(e) => e.stopPropagation()}>
                    {canEdit &&
                        <button type="button" className="accordion-action-icon" aria-label="Edit" onClick={() => edit(item, index, propName)}>
                            <Icon name="edit" size={14} />
                        </button>
                    }
                    <button type="button" className="accordion-action-icon accordion-action-icon--danger" aria-label={item.completed ? "Archive" : "Delete"} onClick={!item.completed ? () => remove(index, propName) : () => archive(index)}>
                        <Icon name="trash" size={14} />
                    </button>
                </div>
                <div className="accordion-content-container">
                    <div className="accordion-content-wrapper">
                        {renderCard(propName, item)}
                        {renderMissingNudge(propName, item)}
                    </div>
                    {(propName === "pastRoles" || propName === "education") && item.description && (
                        <div className="accordion-expanded-container" onClick={(e) => { e.stopPropagation(); onExpand(index, propName); }}>
                            <p>{!expanded.includes(index) && item.description.length > 200 ? item.description.substring(0, 199)+"…" : item.description}</p>
                            {!expanded.includes(index) && item.description.length > 200 && <button type="button" className="accordion-see-more">Read more →</button>}
                            {expanded.includes(index) && item.description.length > 200 && <button type="button" className="accordion-see-more">Show less ↑</button>}
                        </div>
                    )}
                </div>
            </div>
        );
    });

}

/**
 * Render an accordion item as a natural CV-style card: prominent title,
 * one secondary line, a small dates eyebrow, and (where present) an
 * email/phone meta line. Missing fields are quietly dropped - they get
 * called out separately by renderMissingNudge below.
 */
const renderCard = (propName, item) => {

    if (propName === "pastRoles") {
        const period = handleTimePeriod(item.fromDate, item.toDate, item.present, false);
        return (
            <>
                {period && period !== "-" && <p className="accordion-card-eyebrow">{period}</p>}
                {item.position && <h3 className="accordion-card-title">{item.position}</h3>}
                {item.companyName && <p className="accordion-card-subtitle">{item.companyName}</p>}
            </>
        );
    }

    if (propName === "education") {
        const period = handleTimePeriod(item.fromDate, item.toDate, item.present, true);
        return (
            <>
                {period && period !== "-" && <p className="accordion-card-eyebrow">{period}</p>}
                {item.name && <h3 className="accordion-card-title">{item.name}</h3>}
            </>
        );
    }

    if (propName === "references") {
        const subParts = [item.position, item.companyName].filter(Boolean);
        return (
            <>
                {item.referenceName && <h3 className="accordion-card-title">{item.referenceName}</h3>}
                {subParts.length > 0 && <p className="accordion-card-subtitle">{subParts.join(" · ")}</p>}
                {(item.email || item.contactNumber) && (
                    <p className="accordion-card-meta">
                        {item.email && <span>{item.email}</span>}
                        {item.email && item.contactNumber && <span className="accordion-card-meta-sep"> · </span>}
                        {item.contactNumber && <span>{item.contactNumber}</span>}
                    </p>
                )}
            </>
        );
    }

    return null;

};

/**
 * Identify any important fields missing from this accordion item and show
 * an inline coral nudge so the user knows to come back and fill them in.
 * Returns null when the entry is complete.
 */
const renderMissingNudge = (propName, item) => {

    if (item.completed) return null;

    const missing = [];

    if (propName === "pastRoles" || propName === "education") {
        if (!item.fromDate) missing.push("start date");
        if (!item.toDate && !item.present) missing.push("end date");
    }
    if (propName === "pastRoles") {
        if (!item.companyName) missing.push("yacht / company");
        if (!item.position) missing.push("rank");
    }
    if (propName === "education") {
        if (!item.name) missing.push("school name");
    }
    if (propName === "references") {
        if (!item.referenceName) missing.push("referee name");
        if (!item.email && !item.contactNumber) missing.push("contact details");
    }

    if (missing.length === 0) return null;

    const label = missing.length === 1
        ? `Add ${missing[0]} to complete this entry`
        : `Add ${missing.slice(0, -1).join(", ")} and ${missing[missing.length - 1]} to complete this entry`;

    return (
        <div className="accordion-missing-nudge">
            <Icon name="alert-circle" size={14} />
            <span>{label}</span>
        </div>
    );

};